import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "material-ui-confirm";
import { getStoreAdyenDetails, removeStoreSurcharges } from "apis/paymentAPIs";
import { formatSplitCommission } from "./commissionRates";

const RULE_COLUMNS = [
  { label: "Payment method", value: (rule) => rule.paymentMethod },
  { label: "Card region", value: (rule) => rule.cardRegion || "ANY" },
  { label: "Funding source", value: (rule) => rule.fundingSource },
  { label: "Interaction", value: (rule) => rule.shopperInteraction },
  { label: "Currency", value: (rule) => rule.currency },
  { label: "Commission", value: (rule) => formatSplitCommission(rule.splitLogic && rule.splitLogic.commission) },
];

const surchargeRuleCount = (surcharge) =>
  (surcharge && surcharge.configurations && surcharge.configurations.length) || 0;

const surchargeSummary = (surcharge) => {
  const count = surchargeRuleCount(surcharge);
  if (count === 0) {
    return "None";
  }
  return `${count} card rules, ask confirmation: ${surcharge.askConfirmation ? "yes" : "no"}`;
};

const Section = ({ title, children }) => (
  <>
    <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>{title}</Typography>
    {children}
  </>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const Field = ({ label, value }) => (
  <Typography variant="body2">
    <strong>{label}:</strong> {value || "-"}
  </Typography>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const SplitProfile = ({ profile }) => {
  if (!profile) {
    return <Typography variant="body2">No split configuration profile is assigned to this store.</Typography>;
  }
  return (
    <>
      <Field label="Profile" value={profile.splitConfigurationId} />
      <Field label="Description" value={profile.description} />
      <TableContainer>
        <Table size="small" aria-label="Split rules">
          <TableHead>
            <TableRow>
              {RULE_COLUMNS.map((column) => <TableCell key={column.label}>{column.label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {(profile.rules || []).map((rule, index) => (
              <TableRow key={rule.ruleId || index}>
                {RULE_COLUMNS.map((column) => <TableCell key={column.label}>{column.value(rule)}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

SplitProfile.propTypes = {
  profile: PropTypes.shape({
    splitConfigurationId: PropTypes.string,
    description: PropTypes.string,
    rules: PropTypes.array,
  }),
};

const AdyenConfigBody = ({ details, onRemoveSurcharges, isRemoving }) => {
  const { store, splitProfile, terminalSettings } = details;
  const standalone = terminalSettings.standalone || {};
  return (
    <>
      <Section title="Store">
        <Field label="Store" value={`${store.reference} (${store.id})`} />
        <Field label="Status" value={store.status} />
        <Field label="Balance account" value={store.balanceAccountId} />
      </Section>
      <Section title="Split configuration profile">
        <SplitProfile profile={splitProfile} />
      </Section>
      <Section title="Terminal settings (store level)">
        <Field label="Standalone" value={standalone.enableStandalone ? "Enabled" : "Disabled"} />
        <Field label="Surcharge" value={surchargeSummary(terminalSettings.surcharge)} />
        <Button
          variant="outlined"
          color="error"
          size="small"
          style={{ marginTop: 8 }}
          onClick={onRemoveSurcharges}
          disabled={isRemoving || surchargeRuleCount(terminalSettings.surcharge) === 0}
        >
          Remove Surcharges
          {isRemoving && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
        </Button>
        <pre
          data-testid="terminal-settings-json"
          style={{ maxHeight: 320, overflow: "auto", fontSize: 12, background: "rgba(0,0,0,0.04)", padding: 8 }}
        >
          {JSON.stringify(terminalSettings, null, 2)}
        </pre>
      </Section>
    </>
  );
};

AdyenConfigBody.propTypes = {
  details: PropTypes.shape({
    store: PropTypes.object.isRequired,
    splitProfile: PropTypes.object,
    terminalSettings: PropTypes.object.isRequired,
  }).isRequired,
  onRemoveSurcharges: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
};

const removalMessage = (result) => {
  if (result.removed) {
    const overridden = result.terminals.filter((terminal) => terminal.cleared).length;
    return overridden > 0
      ? `Surcharges removed from the store and ${overridden} terminal(s) with their own settings`
      : "Surcharges removed";
  }
  const terminals = result.terminals.filter((terminal) => terminal.remainingRules > 0).map((terminal) => terminal.id);
  return `Adyen still has surcharges: store ${result.storeRemainingRules} rules` +
    (terminals.length > 0 ? `, terminals ${terminals.join(", ")}` : "");
};

// What Adyen has for this merchant right now, read live each time the dialog opens.
const AdyenConfigDialog = ({ open, onClose, restaurantId, showSuccessMessage, showErrorMessage }) => {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { isFetching, isError, error, data, refetch } = useQuery({
    queryKey: ["storeAdyenDetails", restaurantId],
    queryFn: () => getStoreAdyenDetails(restaurantId),
    enabled: open && Boolean(restaurantId),
    staleTime: 0,
    retry: false,
  });

  const errorDetail = error?.response?.data?.error || error?.message;

  const removal = useMutation({
    mutationFn: () => removeStoreSurcharges(restaurantId),
    onSuccess: (result) => {
      (result.removed ? showSuccessMessage : showErrorMessage)(removalMessage(result));
      refetch();
      queryClient.invalidateQueries({ queryKey: ["storeSettings"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.error;
      showErrorMessage(detail ? `Failed to remove surcharges: ${detail}` : "Failed to remove surcharges");
    },
  });

  const handleRemoveSurcharges = () => {
    confirm({
      title: "Remove surcharges?",
      description:
        `This removes every surcharge setting from ${data.store.reference} and its terminals in Adyen. ` +
        "Customers stop paying a card surcharge at this merchant's terminals straight away.",
      confirmationText: "Remove",
    })
      .then(() => removal.mutate())
      .catch(() => {
        /* cancelled */
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="adyen-config-dialog-title">
      <DialogTitle id="adyen-config-dialog-title">Adyen Split Profile & Terminal Settings</DialogTitle>
      <DialogContent>
        {isFetching && <CircularProgress size={30} />}
        {!isFetching && isError && (
          <Typography color="error">Failed to load Adyen details: {errorDetail}</Typography>
        )}
        {!isFetching && data && (
          <AdyenConfigBody
            details={data}
            onRemoveSurcharges={handleRemoveSurcharges}
            isRemoving={removal.isPending}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => refetch()} disabled={isFetching} color="primary">
          Refresh
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdyenConfigDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  restaurantId: PropTypes.string,
  showSuccessMessage: PropTypes.func.isRequired,
  showErrorMessage: PropTypes.func.isRequired,
};

export default AdyenConfigDialog;
